import { supabase } from '../lib/supabase';

export const componentService = {
  async getAll(filters = {}) {
    // Query seller_components as the primary source for the marketplace
    // and join with components (global definition) and products (via product_components cross-ref)
    let query = supabase
      ?.from('seller_components')
            ?.select(`
        *,
        manufacturing_options(
          offered_methods,
          mfg_3d_types,
          mfg_3d_materials,
          mfg_3d_colors,
          mfg_cnc_types,
          mfg_cnc_materials,
          mfg_cnc_colors,
          stl_file_path,
          is_painted,
          painted_colors
        ),
        component:components!inner(
          *,
          manufacturer:manufacturers(id, name, slug),
          product_links:product_components(
            product:products(id, name, slug, gltf_file_path, thumbnail_path, category_id)
          )
        ),
        seller:seller_accounts(id, business_name, business_slug, rating)
      `)
      ?.or('isdeleted.is.null,isdeleted.eq.false');

    if (filters?.includeInactive !== true) {
      query = query.eq('status', 'active');
    }

    // Apply sorting based on seller_components fields
    if (filters?.sortBy) {
      switch (filters.sortBy) {
        case 'price-low':
          query = query.order('price', { ascending: true });
          break;
        case 'price-high':
          query = query.order('price', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        default:
          query = query.order('display_order', { ascending: true }).order('created_at', { ascending: false });
      }
    } else {
      query = query.order('display_order', { ascending: true }).order('created_at', { ascending: false });
    }

    // Apply seller filter
    if (filters?.sellerId) {
      query = query?.eq('seller_id', filters?.sellerId);
    }

    

    // Apply product filter (find components linked to this product via cross-reference table)
    if (filters?.productId) {
      query = query.eq('component.product_id', filters.productId);
    }
    
    // Apply manufacturer filter
    if (filters?.manufacturerIds?.length > 0) {
      query = query?.in('component.manufacturer_id', filters?.manufacturerIds);
    }

    // Apply category filter (via linked products)
    if (filters?.categoryIds?.length > 0) {
      // Find products in these categories
      const { data: catProds } = await supabase.from('products').select('id').in('category_id', filters.categoryIds);
      const catProdIds = catProds?.map(p => p.id) || [];
      if (catProdIds.length > 0) {
         // Find components linked to these products
         const { data: catComps } = await supabase.from('components').select('id').in('product_id', catProdIds);
         const catCompIds = catComps?.map(c => c.id) || [];
         if (catCompIds.length > 0) {
             query = query.in('component_id', catCompIds);
         } else {
             // No components match this category
             query = query.in('component_id', ['00000000-0000-0000-0000-000000000000']);
         }
      } else {
         query = query.in('component_id', ['00000000-0000-0000-0000-000000000000']);
      }
    }

    // Apply price filters
    if (filters?.priceMin !== undefined && filters?.priceMin !== null) {
      query = query?.gte('price', filters?.priceMin);
    }
    if (filters?.priceMax !== undefined && filters?.priceMax !== null) {
      query = query?.lte('price', filters?.priceMax);
    }

    // Apply holiday and season filters
    if (filters?.holiday) {
      const hSearch = `"%${String(filters.holiday).toLowerCase().replace(/"/g, '""')}%"`;
      query = query?.or(`holiday_setting.ilike.${hSearch},keywords.ilike.${hSearch},custom_name.ilike.${hSearch}`);
    }

    if (filters?.season) {
      const sSearch = `"%${String(filters.season).toLowerCase().replace(/"/g, '""')}%"`;
      query = query?.or(`seasonal_setting.ilike.${sSearch},keywords.ilike.${sSearch},custom_name.ilike.${sSearch}`);
    }
    
    // Apply searchTerm
    if (filters?.searchTerm) {
      const words = filters.searchTerm.trim().split(/\s+/).filter(w => w.length > 0);
      
      if (words.length > 0) {
        // 1. Find matching Products (Name or Description) for ANY word
        let prodQuery = supabase.from('products').select('id');
        const prodOrs = words.map(w => `name.ilike."%${w.toLowerCase().replace(/"/g, '""')}%",description.ilike."%${w.toLowerCase().replace(/"/g, '""')}%"`).join(',');
        prodQuery = prodQuery.or(prodOrs);
        const { data: matchingProds } = await prodQuery.limit(1000);
        const prodIds = matchingProds?.map(p => p.id) || [];

        // 2. Find matching Global Components for ANY word
        let compQuery = supabase.from('components').select('id');
        const compOrs = words.map(w => `name.ilike."%${w.toLowerCase().replace(/"/g, '""')}%",description.ilike."%${w.toLowerCase().replace(/"/g, '""')}%",manufacturer_part_number.ilike."%${w.toLowerCase().replace(/"/g, '""')}%"`).join(',');
        compQuery = compQuery.or(compOrs);
        const { data: matchingCompsText } = await compQuery.limit(1000);
        
        // Also fetch components belonging to matched products
        let matchingCompsByProduct = [];
        if (prodIds.length > 0) {
            const { data } = await supabase.from('components').select('id').in('product_id', prodIds).limit(1000);
            matchingCompsByProduct = data || [];
        }
        
        // Combine unique component IDs
        const combinedCompIds = [...new Set([
            ...(matchingCompsText?.map(c => c.id) || []),
            ...(matchingCompsByProduct.map(c => c.id))
        ])];
        
        // Construct the OR parts for the local custom_name column
        const customNameOrs = words.map(word => `custom_name.ilike."%${word.toLowerCase().replace(/"/g, '""')}%"`).join(',');
        
        if (combinedCompIds.length > 0) {
          // If combinedCompIds contains invalid UUIDs it will crash the 'in' statement
          // Filter to ensure they look like valid UUIDs
          const validUuids = combinedCompIds.filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id));
          if (validUuids.length > 0) {
             query = query.or(`${customNameOrs},component_id.in.(${validUuids.join(',')})`);
          } else {
             query = query.or(customNameOrs);
          }
        } else {
          query = query.or(customNameOrs);
        }
      }
    }

    const { data, error } = await query.limit(3000);
    
    if (error) throw error;
    
    let processedData = data?.map(row => {
      const component = row.component;
      // Get primary product from the first link, or fallback to the component's legacy product_id
      const primaryLink = component?.product_links?.[0]?.product;
      
      const product = primaryLink ? {
        ...primaryLink,
        modelUrl: primaryLink.gltf_file_path ? supabase.storage.from('product-3d-models').getPublicUrl(primaryLink.gltf_file_path).data.publicUrl : null,
        thumbnailUrl: primaryLink.thumbnail_path ? supabase.storage.from('product-thumbnails').getPublicUrl(primaryLink.thumbnail_path).data.publicUrl : null
      } : null;

      const mfgOpt = Array.isArray(row?.manufacturing_options) ? row.manufacturing_options[0] : row?.manufacturing_options;
      const is3d = Boolean(component?.is_3d_manufacturing) && (
        !mfgOpt || mfgOpt.offered_methods === 'none' || mfgOpt.offered_methods === '3d_only' || mfgOpt.offered_methods === 'both'
      );
      const isCnc = Boolean(component?.is_cnc_capable) && (
        !mfgOpt || mfgOpt.offered_methods === 'none' || mfgOpt.offered_methods === 'cnc_only' || mfgOpt.offered_methods === 'both'
      );

      return {
        id: row?.id, // This is the seller_component_id (The Offering)
        globalComponentId: component?.id,
        globalName: component?.name,
        globalImages: component?.images_url || [],
        slug: component?.slug,
        name: is3d ? component?.name : (row?.custom_name || component?.name),
        description: component?.description,
        price: row?.price,
        stockQuantity: row?.stock_quantity,
        sku: component?.manufacturer_part_number,

        manufacturer: row?.manufacturer || component?.manufacturer,
        manufacturerName: row?.manufacturer?.name || component?.manufacturer?.name || component?.manufacturer_name,
        manufacturerId: row?.manufacturer_id || component?.manufacturer_id,
        isAccessory: Boolean(row?.is_accessory || component?.is_accessory), // Symmetrical accessory mapping!
        is_accessory: Boolean(row?.is_accessory || component?.is_accessory), // Symmetrical accessory mapping!
        gltfFilePath: component?.stl_file_path || '', // Symmetrical visual swap GLB mapping!
        gltf_file_path: component?.stl_file_path || '', // Symmetrical visual swap GLB mapping!
        glbUrl: row?.image_url || component?.stl_file_path || '', // Symmetrical: Map public GLB visual URL!
        image_url: row?.image_url || '', // Symmetrical: Map image/model URL!
        productId: product?.id,
        product: product,
        allProductLinks: component?.product_links?.map(l => l.product?.id) || [],
        specifications: component?.specifications,
        isActive: row?.status === 'active',
        images: row?.images_url?.length > 0 ? row.images_url : (component?.images_url || []),
        sellerId: row?.seller?.id,
        storeName: row?.seller?.business_name || 'OpenGadget',
        storeSlug: row?.seller?.business_slug,
        condition: row?.condition || 'New',
        storeRating: row?.seller?.rating || 0,
        purchaseLimit: row?.purchase_limit,
        coreCharge: row?.core_charge,
        isSelfManufacturing: row?.is_self_manufacturing !== false,
        fulfillmentPartnerId: row?.manufacturer_id || '',
        is3dManufacturing: is3d,
        globalIs3dManufacturing: Boolean(component?.is_3d_manufacturing),
        globalIsCncCapable: Boolean(component?.is_cnc_capable),
        preferred3dMethod: row?.preferred_3d_method || '',
        mfg3dTypes: mfgOpt ? (mfgOpt.mfg_3d_types || []) : (component?.mfg_3d_types || []),
        mfg3dMaterials: mfgOpt ? (mfgOpt.mfg_3d_materials || []) : (component?.mfg_3d_materials || []),
        mfg3dColors: mfgOpt ? (mfgOpt.mfg_3d_colors || {}) : (component?.mfg_3d_colors || {}),
        isCncCapable: isCnc,
        cncMaterial: row?.cnc_material || '',
        cncGrade: row?.cnc_grade || '',
        mfgCncTypes: mfgOpt ? (mfgOpt.mfg_cnc_types || []) : (component?.mfg_cnc_types || []),
        mfgCncMaterials: mfgOpt ? (mfgOpt.mfg_cnc_materials || []) : (component?.mfg_cnc_materials || []),
        mfgCncColors: mfgOpt ? (mfgOpt.mfg_cnc_colors || {}) : (component?.mfg_cnc_colors || {}),
        isPainted: mfgOpt ? (mfgOpt.is_painted || false) : false,
        is_painted: mfgOpt ? (mfgOpt.is_painted || false) : false,
        paintedColors: mfgOpt ? (mfgOpt.painted_colors || []) : [],
        painted_colors: mfgOpt ? (mfgOpt.painted_colors || []) : [],
        globalGenuinePart: component?.genuine_part,
        globalStaffpick: component?.staffpick,
        genuinePart: row?.genuine_part,
                staffpick: row?.staffpick,
        averageRating: Number(row?.average_rating || component?.average_rating) || 0,
        totalRatings: Number(row?.total_ratings || component?.total_ratings) || 0,
        customName: row?.custom_name,
        viewsCount: Number(row?.views_count) || 0,
        returnperiod: row?.returnperiod,
        shippingWeight: parseFloat(row?.shipping_weight) || 0,
        shippingLength: parseFloat(row?.shipping_length) || 0,
        shippingWidth: parseFloat(row?.shipping_width) || 0,
        shippingHeight: parseFloat(row?.shipping_height) || 0,
        keywords: row?.keywords || component?.keywords || '',
        seasonalSetting: row?.seasonal_setting || component?.seasonal_setting || '',
        holidaySetting: row?.holiday_setting || component?.holiday_setting || ''
      };
    });

    if (filters?.searchTerm) {
      const words = filters.searchTerm.trim().split(/\s+/).filter(w => w.length > 0);
      if (words.length > 1) {
        const strictMatches = processedData.filter(item => {
          const searchableText = [
            item.name,
            item.globalName,
            item.description,
            item.sku,
            item.product?.name,
            item.product?.description
          ].filter(Boolean).join(' ').toLowerCase();

          return words.every(word => searchableText.includes(word.toLowerCase()));
        });
        
        if (strictMatches.length > 0) {
            processedData = strictMatches;
        } else {
            processedData = processedData.map(item => ({ ...item, isPartialMatch: true }));
        }
      }
    }



    return processedData;
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('seller_components')
      ?.select(`
        *,
        manufacturing_options(
          offered_methods,
          mfg_3d_types,
          mfg_3d_materials,
          mfg_3d_colors,
          mfg_cnc_types,
          mfg_cnc_materials,
          mfg_cnc_colors,
          stl_file_path,
          is_painted,
          painted_colors
        ),
        component:components(
          *,
          is_3d_manufacturing,
          preferred_3d_method,
          mfg_3d_types,
          mfg_3d_materials,
          mfg_3d_colors,
          is_cnc_capable,
          cnc_material,
          cnc_grade,
          mfg_cnc_types,
          mfg_cnc_materials,
          mfg_cnc_colors,
          manufacturer:manufacturers(id, name, slug),
          product_links:product_components(
            product:products(id, name, slug, gltf_file_path, thumbnail_path)
          )
        ),
        seller:seller_accounts(id, business_name, business_slug, rating)
      `)
      ?.eq('id', id)
      ?.single();

    if (data) {
      // Increment views on seller component and parent global component asynchronously
      supabase?.rpc('increment_seller_component_views', { seller_component_id: id })?.then(undefined, err => {
        console.error('Error incrementing seller component views:', err);
      });
      if (data.component_id) {
        supabase?.rpc('increment_component_views', { component_id: data.component_id })?.then(undefined, err => {
          console.error('Error incrementing parent global component views:', err);
        });
      }
    }
    
    if (error || !data) {
      // Symmetrical Fallback: Try fetching as a global catalog component directly from the components table!
      const { data: globalData } = await supabase
        ?.from('components')
        ?.select(`
          *,
          manufacturer:manufacturers(id, name, slug)
        `)
        ?.eq('id', id)
        ?.maybeSingle();

      if (globalData) {
        // Increment global component views count
        supabase?.rpc('increment_component_views', { component_id: id })?.then(undefined, err => {
          console.error('Error incrementing global component views:', err);
        });

        return {
          id: null,
          globalComponentId: globalData.id,
          globalName: globalData.name,
          globalImages: globalData.images_url || [],
          name: globalData.name,
          description: globalData.description,
          price: 0,
          sku: globalData.manufacturer_part_number,
          manufacturer: globalData.manufacturer,
          manufacturerName: globalData.manufacturer?.name || globalData.manufacturer_name,
          manufacturerId: globalData.manufacturer_id,
          is3dManufacturing: Boolean(globalData.is_3d_manufacturing),
          isCncCapable: Boolean(globalData.is_cnc_capable),
          mfg3dTypes: globalData.mfg_3d_types || [],
          mfg3dMaterials: globalData.mfg_3d_materials || [],
          mfg3dColors: globalData.mfg_3d_colors || {},
          mfgCncTypes: globalData.mfg_cnc_types || [],
          mfgCncMaterials: globalData.mfg_cnc_materials || [],
          mfgCncColors: globalData.mfg_cnc_colors || {},
          component: globalData,
          globalItem: globalData
        };
      }
      throw error || new Error('Offering not found');
    }
    
    const component = data.component;
    const primaryLink = component?.product_links?.[0]?.product;

    const product = primaryLink ? {
      ...primaryLink,
      modelUrl: primaryLink.gltf_file_path ? supabase.storage.from('product-3d-models').getPublicUrl(primaryLink.gltf_file_path).data.publicUrl : null,
      thumbnailUrl: primaryLink.thumbnail_path ? supabase.storage.from('product-thumbnails').getPublicUrl(primaryLink.thumbnail_path).data.publicUrl : null
    } : null;

    const mfgOpt = Array.isArray(data?.manufacturing_options) ? data.manufacturing_options[0] : data?.manufacturing_options;
    const is3d = Boolean(component?.is_3d_manufacturing) && (
      !mfgOpt || mfgOpt.offered_methods === 'none' || mfgOpt.offered_methods === '3d_only' || mfgOpt.offered_methods === 'both'
    );
    const isCnc = Boolean(component?.is_cnc_capable) && (
      !mfgOpt || mfgOpt.offered_methods === 'none' || mfgOpt.offered_methods === 'cnc_only' || mfgOpt.offered_methods === 'both'
    );

    return {
      id: data?.id,
      globalComponentId: component?.id,
      globalName: component?.name,
      globalImages: component?.images_url || [],
      slug: component?.slug,
      name: is3d ? component?.name : (data?.custom_name || component?.name),
      description: component?.description,
      price: data?.price,
      stockQuantity: data?.stock_quantity,
      sku: component?.manufacturer_part_number,
      manufacturer: component?.manufacturer || data?.manufacturer,
      manufacturerName: component?.manufacturer?.name || data?.manufacturer?.name || component?.manufacturer_name,
      manufacturerId: component?.manufacturer_id || data?.manufacturer_id,
      isAccessory: Boolean(data?.is_accessory || component?.is_accessory), // Symmetrical accessory mapping!
      is_accessory: Boolean(data?.is_accessory || component?.is_accessory), // Symmetrical accessory mapping!
      gltfFilePath: component?.stl_file_path || '', // Symmetrical visual swap GLB mapping!
      gltf_file_path: component?.stl_file_path || '', // Symmetrical visual swap GLB mapping!
      glbUrl: data?.image_url || component?.stl_file_path || '', // Symmetrical: Map public GLB visual URL!
      image_url: data?.image_url || '', // Symmetrical: Map image/model URL!
      productId: product?.id,
      product: product,
      component: component,
      specifications: component?.specifications,
      isActive: data?.status === 'active',
      images: data?.images_url?.length > 0 ? data.images_url : (component?.images_url || []),
      sellerId: data?.seller?.id,
      storeName: data?.seller?.business_name || 'OpenGadget',
      storeSlug: data?.seller?.business_slug,
      storeRating: data?.seller?.rating || 0,
      purchaseLimit: data?.purchase_limit,
      coreCharge: data?.core_charge,
      is3dManufacturing: is3d,
      preferred3dMethod: data?.preferred_3d_method ?? component?.preferred_3d_method,
      mfg3dTypes: mfgOpt ? (mfgOpt.mfg_3d_types || []) : (component?.mfg_3d_types || []),
      mfg3dMaterials: mfgOpt ? (mfgOpt.mfg_3d_materials || []) : (component?.mfg_3d_materials || []),
      mfg3dColors: mfgOpt ? (mfgOpt.mfg_3d_colors || {}) : (component?.mfg_3d_colors || {}),
      isCncCapable: isCnc,
      cncMaterial: data?.cnc_material ?? component?.cnc_material,
      cncGrade: data?.cnc_grade ?? component?.cnc_grade,
      mfgCncTypes: mfgOpt ? (mfgOpt.mfg_cnc_types || []) : (component?.mfg_cnc_types || []),
      mfgCncMaterials: mfgOpt ? (mfgOpt.mfg_cnc_materials || []) : (component?.mfg_cnc_materials || []),
      mfgCncColors: mfgOpt ? (mfgOpt.mfg_cnc_colors || {}) : (component?.mfg_cnc_colors || {}),
      isPainted: mfgOpt ? (mfgOpt.is_painted || false) : false,
      is_painted: mfgOpt ? (mfgOpt.is_painted || false) : false,
      paintedColors: mfgOpt ? (mfgOpt.painted_colors || []) : [],
      painted_colors: mfgOpt ? (mfgOpt.painted_colors || []) : [],
      globalGenuinePart: component?.genuine_part,
      globalStaffpick: component?.staffpick,
      genuinePart: data?.genuine_part,
      staffpick: data?.staffpick,
      averageRating: Number(data?.average_rating || component?.average_rating) || 0,
      totalRatings: Number(data?.total_ratings || component?.total_ratings) || 0,
      customName: data?.custom_name,
      returnperiod: data?.returnperiod,
      shippingWeight: parseFloat(data?.shipping_weight) || 0,
      shippingLength: parseFloat(data?.shipping_length) || 0,
      shippingWidth: parseFloat(data?.shipping_width) || 0,
      shippingHeight: parseFloat(data?.shipping_height) || 0,
      condition: data?.condition || 'New'
    };
  },

  async getBySlug(slug) {
    const { data, error } = await supabase
      ?.from('components')
      ?.select('id')
      ?.eq('slug', slug)
      ?.single();
    
    if (error) throw error;
    
    // Once we have global ID, we find active seller offerings.
    // This is tricky because one slug points to one global component but many seller offerings.
    // For SEO details page, we probably want the "main" or cheapest offering.
    const offerings = await this.getAll({ globalComponentId: data.id });
    return offerings[0] || null;
  },

  async getByProductId(productId) {
    const { data, error } = await supabase
      ?.from('seller_components')
      ?.select(`
        *,
        manufacturing_options(
          offered_methods,
          mfg_3d_types,
          mfg_3d_materials,
          mfg_3d_colors,
          mfg_cnc_types,
          mfg_cnc_materials,
          mfg_cnc_colors,
          stl_file_path,
          is_painted,
          painted_colors
        ),
        component:components(
          *,
          manufacturer:manufacturers(id, name, slug),
          product_links:product_components(
            product:products(id, name, slug, gltf_file_path, thumbnail_path)
          )
        ),
        seller:seller_accounts(id, business_name, business_slug, rating)
        `)
        ?.eq('status', 'active')
        ?.or('isdeleted.is.null,isdeleted.eq.false')
        ?.order('display_order', { ascending: true })
        ?.order('price', { ascending: true });

        if (error) throw error;

        const filteredData = data?.filter(row => {
        const comp = row.component;
        if (!comp) return false;

        // Symmetrical: Filter direct product_id links or cross-reference product_components links natively inside Javascript!
        const isDirectMatch = comp.product_id?.toString().toLowerCase() === productId?.toString().toLowerCase();
        const isCrossMatch = comp.product_links?.some(link => link.product?.id?.toString().toLowerCase() === productId?.toString().toLowerCase());

        return isDirectMatch || isCrossMatch;
        }) || [];

        return filteredData.map(row => {
      const component = row.component;
      const primaryLink = component?.product_links?.[0]?.product;

      const product = primaryLink ? {
        ...primaryLink,
        modelUrl: primaryLink.gltf_file_path ? supabase.storage.from('product-3d-models').getPublicUrl(primaryLink.gltf_file_path).data.publicUrl : null,
        thumbnailUrl: primaryLink.thumbnail_path ? supabase.storage.from('product-thumbnails').getPublicUrl(primaryLink.thumbnail_path).data.publicUrl : null
      } : null;

      const mfgOpt = Array.isArray(row?.manufacturing_options) ? row.manufacturing_options[0] : row?.manufacturing_options;
      const is3d = Boolean(component?.is_3d_manufacturing) && (
        !mfgOpt || mfgOpt.offered_methods === 'none' || mfgOpt.offered_methods === '3d_only' || mfgOpt.offered_methods === 'both'
      );
      const isCnc = Boolean(component?.is_cnc_capable) && (
        !mfgOpt || mfgOpt.offered_methods === 'none' || mfgOpt.offered_methods === 'cnc_only' || mfgOpt.offered_methods === 'both'
      );

      return {
        id: row?.id,
        globalComponentId: component?.id,
        globalName: component?.name,
        globalImages: component?.images_url || [],
        slug: component?.slug,
        name: is3d ? component?.name : (row?.custom_name || component?.name),
        description: component?.description,
        price: row?.price,
        stockQuantity: row?.stock_quantity,
        sku: component?.manufacturer_part_number,
        manufacturer: component?.manufacturer,
        manufacturerName: row?.manufacturer?.name || component?.manufacturer?.name || component?.manufacturer_name,
        manufacturerId: row?.manufacturer_id || component?.manufacturer_id,
        isAccessory: Boolean(row?.is_accessory || component?.is_accessory), // Symmetrical accessory mapping!
        is_accessory: Boolean(row?.is_accessory || component?.is_accessory), // Symmetrical accessory mapping!
        gltfFilePath: component?.stl_file_path || '', // Symmetrical visual swap GLB mapping!
        gltf_file_path: component?.stl_file_path || '', // Symmetrical visual swap GLB mapping!
        glbUrl: row?.image_url || component?.stl_file_path || '', // Symmetrical: Map public GLB visual URL!
        image_url: row?.image_url || '', // Symmetrical: Map image/model URL!
        productId: product?.id,
        product: product,
        specifications: component?.specifications,
        isActive: row?.status === 'active',
        images: row?.images_url?.length > 0 ? row.images_url : (component?.images_url || []),
        sellerId: row?.seller?.id,
        storeName: row?.seller?.business_name || 'OpenGadget',
        storeSlug: row?.seller?.business_slug,
        condition: row?.condition || 'New',
        storeRating: row?.seller?.rating || 0,
        purchaseLimit: row?.purchase_limit,
        coreCharge: row?.core_charge,
        isSelfManufacturing: row?.is_self_manufacturing !== false,
        fulfillmentPartnerId: row?.manufacturer_id || '',
        is3dManufacturing: is3d,
        globalIs3dManufacturing: Boolean(component?.is_3d_manufacturing),
        globalIsCncCapable: Boolean(component?.is_cnc_capable),
        preferred3dMethod: row?.preferred_3d_method || '',
        mfg3dTypes: mfgOpt ? (mfgOpt.mfg_3d_types || []) : (component?.mfg_3d_types || []),
        mfg3dMaterials: mfgOpt ? (mfgOpt.mfg_3d_materials || []) : (component?.mfg_3d_materials || []),
        mfg3dColors: mfgOpt ? (mfgOpt.mfg_3d_colors || {}) : (component?.mfg_3d_colors || {}),
        isCncCapable: isCnc,
        cncMaterial: row?.cnc_material || '',
        cncGrade: row?.cnc_grade || '',
        mfgCncTypes: mfgOpt ? (mfgOpt.mfg_cnc_types || []) : (component?.mfg_cnc_types || []),
        mfgCncMaterials: mfgOpt ? (mfgOpt.mfg_cnc_materials || []) : (component?.mfg_cnc_materials || []),
        mfgCncColors: mfgOpt ? (mfgOpt.mfg_cnc_colors || {}) : (component?.mfg_cnc_colors || {}),
        isPainted: mfgOpt ? (mfgOpt.is_painted || false) : false,
        is_painted: mfgOpt ? (mfgOpt.is_painted || false) : false,
        paintedColors: mfgOpt ? (mfgOpt.painted_colors || []) : [],
        painted_colors: mfgOpt ? (mfgOpt.painted_colors || []) : [],
        globalGenuinePart: component?.genuine_part,
        globalStaffpick: component?.staffpick,
        genuinePart: row?.genuine_part,
        staffpick: row?.staffpick,
        averageRating: Number(row?.average_rating || component?.average_rating) || 0,
        totalRatings: Number(row?.total_ratings || component?.total_ratings) || 0,
        customName: row?.custom_name,
        viewsCount: Number(row?.views_count) || 0,
        returnperiod: row?.returnperiod,
        shippingWeight: parseFloat(row?.shipping_weight) || 0,
        shippingLength: parseFloat(row?.shipping_length) || 0,
        shippingWidth: parseFloat(row?.shipping_width) || 0,
        shippingHeight: parseFloat(row?.shipping_height) || 0,
        keywords: row?.keywords || component?.keywords || '',
        seasonalSetting: row?.seasonal_setting || component?.seasonal_setting || '',
        holidaySetting: row?.holiday_setting || component?.holiday_setting || ''
      };
    });
  },

  async create(component) {
    let globalComp;

    // 1. Deduplicate by Name AND Product ID
    if (component?.productId && component?.name) {
      // Find if this product already has a component with this exact name
      const { data: existingCrossRefs, error: searchError } = await supabase
        .from('product_components')
        .select('component_id, component:components!inner(name)')
        .eq('product_id', component.productId)
        .ilike('components.name', component.name)
        .limit(1)
        .maybeSingle();

      if (!searchError && existingCrossRefs && existingCrossRefs.component_id) {
        // We found an existing global component for this product with this name
        const { data: existingComp } = await supabase
           .from('components')
           .select('*')
           .eq('id', existingCrossRefs.component_id)
           .single();
           
        if (existingComp) {
            globalComp = existingComp;
              console.log('componentService.create - Found existing global component linked to this product:', globalComp.name);
          }
        }
      }

    // 2. Create Global Component if not found
    if (!globalComp) {
      const { data: newComp, error: globalError } = await supabase
        .from('components')
        .insert({
          name: component?.name,
          slug: component?.slug || null,
          price: 0, // Satisfy legacy NOT NULL constraint
          description: component?.description || null,
          manufacturer_part_number: component?.manufacturerPartNumber || null,
          upc: component?.upc || null,
          manufacturer_id: component?.manufacturerId || null,
          product_id: component?.productId || null, // Primary legacy assignment
          specifications: component?.specifications || {},
          is_3d_manufacturing: Boolean(component?.is3dManufacturing),
          preferred_3d_method: component?.is3dManufacturing ? (component?.preferred3dMethod || null) : null,
          mfg_3d_types: component?.mfg3dTypes || [],
          mfg_3d_materials: component?.mfg3dMaterials || [],
          mfg_3d_colors: component?.mfg3dColors || {},
          is_cnc_capable: Boolean(component?.isCncCapable),
          cnc_material: component?.isCncCapable ? (component?.cncMaterial || null) : null,
          cnc_grade: component?.isCncCapable ? (component?.cncGrade || null) : null,
          mfg_cnc_types: component?.mfgCncTypes || [],
          mfg_cnc_materials: component?.mfgCncMaterials || [],
          mfg_cnc_colors: component?.mfgCncColors || {},
          created_by: component?.createdBy || null,

          returnperiod: component?.returnperiod !== undefined && component?.returnperiod !== '' && component?.returnperiod !== null ? parseInt(component.returnperiod, 10) : 30,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (globalError) throw globalError;
      globalComp = newComp;
      console.log('componentService.create - Created new global component:', globalComp.name);
    }

    // 3. Create Product Link in cross-reference table
    if (component?.productId) {
      const { data: existingLink } = await supabase
        .from('product_components')
        .select('id')
        .eq('product_id', component.productId)
        .eq('component_id', globalComp.id)
        .maybeSingle();

      if (!existingLink) {
        await supabase.from('product_components').insert({
          product_id: component.productId,
          component_id: globalComp.id
        });
      }
    }

    // 3. Create Seller Offering
    const { data: offering, error: offeringError } = await supabase
      .from('seller_components')
      .insert({
        seller_id: component?.sellerId || null,
        component_id: globalComp.id,
        price: parseFloat(component?.price || 0),
        stock_quantity: parseInt(component?.stock_quantity || component?.stockQuantity || 0, 10) || 0,
        custom_name: component?.customName || null,
        status: component?.isActive !== false ? 'active' : 'inactive',
        condition: component?.condition || 'New',
        images_url: component?.images || [],
        shipping_weight: parseFloat(component?.shippingWeight) || 0,
        shipping_length: parseFloat(component?.shippingLength) || 0,
        shipping_width: parseFloat(component?.shippingWidth) || 0,
        shipping_height: parseFloat(component?.shippingHeight) || 0,
        returnperiod: component?.returnperiod ? parseInt(component.returnperiod, 10) : 30,
        genuine_part: Boolean(component?.genuinePart),
        staffpick: Boolean(component?.staffpick),
        is_self_manufacturing: Boolean(component?.isSelfManufacturing),
        manufacturer_id: component?.isSelfManufacturing ? null : (component?.fulfillmentPartnerId || null),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isdeleted: false
      })
      .select()
      .single();

    if (offeringError) throw offeringError;

    return await this.getById(offering.id);
  },

  async update(id, component) {
      console.log('componentService.update called with id:', id, 'payload:', component);

      // Symmetrical: Retrieve and update the linked global components table record first!
      try {
        const { data: currentOffering } = await supabase
          .from('seller_components')
          .select('component_id')
          .eq('id', id)
          .maybeSingle();

        if (currentOffering?.component_id) {
          const globalUpdatePayload = {
            name: component?.name,
            description: component?.description || null,
            keywords: component?.keywords || '',
            seasonal_setting: component?.seasonalSetting || component?.seasonal_setting || '',
            holiday_setting: component?.holidaySetting || component?.holiday_setting || '',
            manufacturer_part_number: component?.manufacturerPartNumber || null,
            manufacturer_id: component?.manufacturerId || null,
            specifications: component?.specifications || {},
            is_3d_manufacturing: Boolean(component?.is3dManufacturing),
            preferred_3d_method: component?.is3dManufacturing ? (component?.preferred3dMethod || null) : null,
            mfg_3d_types: component?.mfg3dTypes || [],
            mfg_3d_materials: component?.mfg3dMaterials || [],
            mfg_3d_colors: component?.mfg3dColors || {},
            is_cnc_capable: Boolean(component?.isCncCapable),
            cnc_material: component?.isCncCapable ? (component?.cncMaterial || null) : null,
            cnc_grade: component?.isCncCapable ? (component?.cncGrade || null) : null,
            mfg_cnc_types: component?.mfgCncTypes || [],
            mfg_cnc_materials: component?.mfgCncMaterials || [],
            mfg_cnc_colors: component?.mfgCncColors || {},
            returnperiod: component?.returnperiod !== undefined && component?.returnperiod !== '' && component?.returnperiod !== null ? parseInt(component.returnperiod, 10) : 30
          };

          await supabase
            .from('components')
            .update(globalUpdatePayload)
            .eq('id', currentOffering.component_id);
            
          console.log('componentService.update - Successfully updated linked global component!');
        }
      } catch (err) {
        console.error('Error synchronizing global component fields:', err);
      }

      // 1. Update the Seller Offering (the price, stock, status etc)
      const updatePayload = {
          price: parseFloat(component?.price || 0),
          stock_quantity: parseInt(component?.stock_quantity || component?.stockQuantity || 0, 10) || 0,
          custom_name: component?.customName || null,
          status: component?.isActive !== false ? 'active' : 'inactive',
          condition: component?.condition || 'New',
          images_url: component?.images || [],
          keywords: component?.keywords || '',
          seasonal_setting: component?.seasonalSetting || component?.seasonal_setting || '',
          holiday_setting: component?.holidaySetting || component?.holiday_setting || '',
          shipping_weight: parseFloat(component?.shippingWeight) || 0,
          shipping_length: parseFloat(component?.shippingLength) || 0,
          shipping_width: parseFloat(component?.shippingWidth) || 0,
          shipping_height: parseFloat(component?.shippingHeight) || 0,
          returnperiod: component?.returnperiod ? parseInt(component.returnperiod, 10) : null,
          purchase_limit: component?.purchaseLimit !== undefined && component?.purchaseLimit !== null && component?.purchaseLimit !== '' ? parseInt(component.purchaseLimit, 10) : null,
          core_charge: component?.coreCharge !== undefined && component?.coreCharge !== null && component?.coreCharge !== '' ? parseFloat(component.coreCharge) : 0,
          is_self_manufacturing: Boolean(component?.isSelfManufacturing),
          manufacturer_id: component?.isSelfManufacturing ? null : (component?.fulfillmentPartnerId || null),
          updated_at: new Date().toISOString()
      };

      console.log('componentService.update payload to Supabase:', updatePayload);
      
      const { data: offeringRows, error: offeringError } = await supabase
        .from('seller_components')
        .update(updatePayload)
        .eq('id', id)
        .select();
      
      console.log('componentService.update Supabase response:', offeringRows, offeringError);
      
      if (offeringError) throw offeringError;
      
      if (!offeringRows || offeringRows.length === 0) {
        throw new Error(`Failed to update component. The component may not exist or you do not have permission to modify it. ID: ${id}`);
      }

      const offering = offeringRows[0];

      return {
        id: offering.id,
        name: offering.custom_name,
        price: offering.price,
        stockQuantity: offering.stock_quantity,
        isActive: offering.status === 'active'
      };
    },

  async searchGlobalComponents(searchTerm) {
    if (!searchTerm) return [];
    
    const words = searchTerm.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length === 0) return [];
    
    // 1. Find matching Products (Name or Description)
    let prodQuery = supabase.from('products').select('id');
    const prodOrs = words.map(w => `name.ilike."%${w.toLowerCase().replace(/"/g, '""')}%",description.ilike."%${w.toLowerCase().replace(/"/g, '""')}%"`).join(',');
    prodQuery = prodQuery.or(prodOrs);
    const { data: matchingProds } = await prodQuery.limit(1000);
    const prodIds = matchingProds?.map(p => p.id) || [];
    
    // 2. Find matching Global Components (Name, Description, Part Number)
    let compQuery = supabase.from('components').select('id');
    const compOrs = words.map(w => `name.ilike."%${w.toLowerCase().replace(/"/g, '""')}%",description.ilike."%${w.toLowerCase().replace(/"/g, '""')}%",manufacturer_part_number.ilike."%${w.toLowerCase().replace(/"/g, '""')}%"`).join(',');
    compQuery = compQuery.or(compOrs);
    const { data: matchingCompsText } = await compQuery.limit(1000);
    
    // Also fetch components belonging to matched products
    let matchingCompsByProduct = [];
    if (prodIds.length > 0) {
        const { data } = await supabase.from('components').select('id').in('product_id', prodIds).limit(1000);
        matchingCompsByProduct = data || [];
    }
    
    // Combine unique component IDs
    const combinedCompIds = [...new Set([
        ...(matchingCompsText?.map(c => c.id) || []),
        ...(matchingCompsByProduct.map(c => c.id))
    ])];
    
    const validUuids = combinedCompIds.filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id));
    
    if (validUuids.length === 0) return [];

    // 3. Query combined components
    const { data, error } = await supabase
      .from('components')
      .select(`
        *,
        manufacturer:manufacturers(id, name, slug),
        product:products(id, name, slug, thumbnail_path)
      `)
      .in('id', validUuids)
      .limit(1000);

    if (error) {
      console.error('Error searching global components:', error);
      throw error;
    }
    
    let processedData = data.map(c => ({
      ...c,
      productThumbnail: c.product?.thumbnail_path ? supabase.storage.from('product-thumbnails').getPublicUrl(c.product.thumbnail_path).data.publicUrl : null,
      globalGenuinePart: c.genuine_part,
      globalStaffpick: c.staffpick
    }));
    
    if (words.length > 1) {
        const strictMatches = processedData.filter(item => {
             const searchableText = [
                 item.name,
                 item.description,
                 item.manufacturer_part_number,
                 item.product?.name,
                 item.product?.description
             ].filter(Boolean).join(' ').toLowerCase();

             return words.every(word => searchableText.includes(word.toLowerCase()));
        });
        
        if (strictMatches.length > 0) {
            processedData = strictMatches;
        } else {
            processedData = processedData.map(item => ({ ...item, isPartialMatch: true }));
        }
    }

    return processedData.slice(0, 50);
  },

  async createSellerOffering(offeringData) {
    const { data, error } = await supabase
      .from('seller_components')
      .insert({
        seller_id: offeringData.sellerId,
        component_id: offeringData.componentId,
        price: offeringData.price || 0,
        stock_quantity: offeringData.stockQuantity || 0,
        custom_name: offeringData.customName || null,
        status: offeringData.isActive !== false ? 'active' : 'inactive',
        condition: offeringData.condition || 'New',
        images_url: offeringData.images || [],
        shipping_weight: parseFloat(offeringData.shippingWeight) || 0,
        shipping_length: parseFloat(offeringData.shippingLength) || 0,
        shipping_width: parseFloat(offeringData.shippingWidth) || 0,
        shipping_height: parseFloat(offeringData.shippingHeight) || 0,
        returnperiod: offeringData.returnperiod ? parseInt(offeringData.returnperiod, 10) : 30,
        genuine_part: Boolean(offeringData.genuinePart),
        staffpick: Boolean(offeringData.staffpick),
        is_3d_manufacturing: typeof offeringData.is3dManufacturing === 'boolean' ? offeringData.is3dManufacturing : null,
        preferred_3d_method: offeringData.preferred3dMethod || null,
        mfg_3d_types: offeringData.mfg3dTypes || [],
        mfg_3d_materials: offeringData.mfg3dMaterials || [],
        mfg_3d_colors: offeringData.mfg3dColors || {},
        is_cnc_capable: typeof offeringData.isCncCapable === 'boolean' ? offeringData.isCncCapable : null,
        cnc_material: offeringData.cncMaterial || null,
        cnc_grade: offeringData.cncGrade || null,
        mfg_cnc_types: offeringData.mfgCncTypes || [],
        mfg_cnc_materials: offeringData.mfgCncMaterials || [],
        mfg_cnc_colors: offeringData.mfgCncColors || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    // If a productId is provided, ensure this component is linked to that product
    if (offeringData.productId) {
      const { data: existingLink } = await supabase
        .from('product_components')
        .select('id')
        .eq('product_id', offeringData.productId)
        .eq('component_id', offeringData.componentId)
        .maybeSingle();

      if (!existingLink) {
        await supabase.from('product_components').insert({
          product_id: offeringData.productId,
          component_id: offeringData.componentId
        });
      }
    }

    return data;
  },

  async delete(id) {
    // We only mark the seller's specific offering as deleted
    const { error } = await supabase
      .from('seller_components')
      .update({ isdeleted: true })
      .eq('id', id);

    if (error) throw error;
  }};